import { createSlice } from '@reduxjs/toolkit';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import type { JsonFailure, WsMessagePayload } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type {
  JsonTradingSuccess,
  JsonWebMessage,
  JsonWebMessageRender,
  TradingState,
} from 'src/store/shared/tradingBots/types';
import { BotCommands } from 'src/store/shared/tradingBots/types';
import type {
  createSession as createSessionAction,
  sendMessageWs,
} from 'src/store/shared/tradingBots/actions';
import { listen as listenLoans } from 'src/store/loans/actions';
import { listen as listenRetainers } from 'src/store/retainers/actions';
import { listen as listenTrading } from 'src/store/trading/apiKeys/actions';
import { parseMessage } from 'src/store/shared/tradingBots/utils';

registerErrorMessage(BotCommands.CreateSession, {
  title: 'Create Session Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

const allowedCommands = Object.values(BotCommands);

const objListen = {
  loans: listenLoans,
  retainers: listenRetainers,
  trading: listenTrading,
};

export function createBotsSlice({
  prefix,
  sendMessage,
  createSession,
  onMessage,
  initialState,
}: {
  prefix: 'loans' | 'retainers' | 'trading';
  sendMessage: typeof sendMessageWs;
  createSession: typeof createSessionAction;
  onMessage: ActionCreatorWithPayload<WsMessagePayload>;
  initialState: TradingState;
}) {
  return createSlice({
    name: `${prefix}/bots`,
    initialState,
    reducers: {
      resetSession(state) {
        state.messageLog = [];
        state.groupList = null;
        state.sessionDetails = null;
      },
      setMessage(state, action: { payload: JsonWebMessageRender; type: string }) {
        state.messageLog.push(action.payload);
      },
      resetSessionDetails(state) {
        state.sessionDetails = null;
        state.messageLog = [];
      },
    },
    extraReducers: (builder) => {
      // on WS message

      builder.addCase(createSession.pending, (state) => {
        state.sessionCreateStatus = 'pending';
        state.messageLog = [];
        state.sessionDetails = null;
      });

      builder.addCase(createSession.fulfilled, (state, action) => {
        state.sessionDetails = action.payload;
      });

      builder.addCase(objListen[prefix], (state) => {
        state.groupListStatus = 'pending';
      });

      builder.addCase(sendMessage.pending, (state) => {
        state.messageStatus = 'pending';
      });

      builder.addCase(onMessage, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(`${prefix}/bots/processing ${onMessage.toString()}`, action, allowedCommands);

        if (error) {
          state.error = error;

          switch (errorCommand) {
            case undefined:
            default:
          }

          return;
        }

        state.error = null;

        const { command, okCommand } = json;
        if (command === BotCommands.GroupList) {
          state.groupListStatus = 'success';
          const { groups }: JsonTradingSuccess = json;
          state.groupList = groups ?? [];
        }

        if (command === BotCommands.WebMessage) {
          const { session, message, messageID } = json as JsonWebMessage;
          state.messageLog.push({ session, messageID, ...parseMessage(message) });
          state.messageStatus = 'success';
        }

        if (isOkCommand(command)) {
          if (okCommand === BotCommands.WebMessage) {
            const { session, message, messageID } = json as JsonWebMessage;
            if (message) {
              state.messageLog.push({ session, messageID, ...parseMessage(message) });
              state.messageStatus = 'success';
            }
          }

          if (okCommand === BotCommands.CreateSession) {
            // TODO: get session from ok command response. Needed server support. Remove createSession.fulfilled handling after implementation
            // const { session }: JsonTradingCreateSessionSuccess = json;
            state.sessionCreateStatus = 'success';
          }
        }
      });
    },
  });
}
